/**
 * DocumentsAuditPage — admin-only audit log view for the @kgc/documents
 * subsystem. Story 94-9b frontend, Sprint 1 SZL jogi redesign.
 *
 * Access: only ADMIN role (server-enforced on `GET /documents/audit`).
 * The page intentionally does NOT try to hide itself client-side when the
 * user lacks the role — if the API returns 403, we show an error state.
 *
 * Features (Sprint 1 MVP):
 *   - Filter by DocumentAuditAction (dropdown, enum-driven)
 *   - Filter by documentId (explicit UUID input, optional)
 *   - Pagination via skip/take (server hard-caps skip at 100k, take at 500)
 *   - CSV export of currently visible page
 *
 * Deferred to Sprint 2+:
 *   - sourceType / sourceId filter (not yet exposed by backend query DTO)
 *   - date range picker (not yet exposed by backend query DTO)
 *   - tenant selector for multi-tenant ADMINs (Sprint 3+)
 *   - IP / user-agent free-text search
 *
 * @see apps/kgc-api/src/modules/documents/documents.controller.ts — GET /audit
 */

import { type DocumentAuditAction, type DocumentAuditItem } from '@/api/documents';
import { sanitizeCsvValue } from '@/lib/utils';
import { useTranslation } from '@kgc/i18n';
import { Button, toast } from '@kgc/ui';
import { AlertTriangle, Download, Filter, Loader2, RefreshCw } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useDocumentsAudit } from '../../features/documents/hooks/useDocuments';

// Keep this list in sync with the Prisma `DocumentAuditAction` enum
// (schema/enums.prisma).
const AUDIT_ACTIONS: DocumentAuditAction[] = [
  'UPLOADED',
  'DOWNLOADED',
  'VIEWED',
  'SHARED_VIA_LINK',
  'UPDATED',
  'DELETED',
  'RESTORED',
  'HASH_VERIFIED',
  'HASH_MISMATCH',
  'ORPHAN_DETECTED',
];

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

/**
 * UUID v4 regex — same shape the backend's `ParseUUIDPipe({ version: '4' })`
 * enforces. Client validates BEFORE hitting `/documents/audit?documentId=…`
 * so a partially-typed ID doesn't cause the whole page to flip to an
 * "unable to load" error state (adversarial CRITICAL: Blind Hunter finding).
 */
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Format an ISO timestamp for the audit table. */
function formatTs(iso: string, locale: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return iso;
  }
}

/**
 * CSV-safe escaper. Wraps in quotes + doubles any embedded quotes so that
 * Excel/LibreOffice import cleanly.
 *
 * SECURITY (CSV injection defense — adversarial HIGH fix): cells whose first
 * character is `=`, `+`, `-`, `@`, `\t` or `\r` are prefixed with a single
 * quote `'` so Excel/LibreOffice treat them as literal text instead of
 * formulas. Attackers can forge `User-Agent: =HYPERLINK("https://evil/", "x")`
 * on any audit-logged request; without this escape, a subsequent CSV export
 * opened in Excel would execute the formula. OWASP CSV injection (CWE-1236).
 */
function csvCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  const raw = typeof value === 'string' ? value : JSON.stringify(value);
  // Kanonikus guard (lib/utils) — a lokál duplikátum a vezető-szóköz gapet hordozta.
  const s = sanitizeCsvValue(raw);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function toCsv(items: DocumentAuditItem[]): string {
  const header = [
    'id',
    'documentId',
    'action',
    'performedBy',
    'performedAt',
    'ipAddress',
    'userAgent',
    'metadata',
  ];
  const rows = items.map(item =>
    [
      csvCell(item.id),
      csvCell(item.documentId),
      csvCell(item.action),
      csvCell(item.performedBy),
      csvCell(item.performedAt),
      csvCell(item.ipAddress),
      csvCell(item.userAgent),
      csvCell(item.metadata),
    ].join(',')
  );
  return [header.join(','), ...rows].join('\r\n');
}

function downloadAsCsv(csv: string, filename: string): void {
  // Prefix BOM so Excel auto-detects UTF-8 and doesn't mangle Hungarian ékezetek.
  const blob = new Blob(['\ufeff', csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    // Release the blob URL even if link.click() throws. Adversarial MEDIUM fix.
    URL.revokeObjectURL(url);
  }
}

// ═════════════════════════════════════════════════════════════════════════

export function DocumentsAuditPage() {
  const { t, locale } = useTranslation();

  // Filter state
  const [action, setAction] = useState<DocumentAuditAction | 'ALL'>('ALL');
  const [documentId, setDocumentId] = useState<string>('');
  const [pageSize, setPageSize] = useState<number>(50);
  const [page, setPage] = useState<number>(0);

  // documentId validation — only pass through a well-formed UUID to the
  // backend. A partial string produces no filter (empty = "all docs") and
  // the UI shows an inline error. Adversarial CRITICAL fix.
  const documentIdTrimmed = documentId.trim();
  const documentIdIsValid = documentIdTrimmed === '' || UUID_REGEX.test(documentIdTrimmed);

  // Stable query object so react-query caches each page separately.
  const query = useMemo(
    () => ({
      ...(action !== 'ALL' && { action }),
      ...(documentIdIsValid && documentIdTrimmed !== '' && { documentId: documentIdTrimmed }),
      skip: page * pageSize,
      take: pageSize,
    }),
    [action, documentIdTrimmed, documentIdIsValid, page, pageSize]
  );

  const { data, isLoading, isError, refetch, isFetching } = useDocumentsAudit(query);

  const handleResetFilters = useCallback(() => {
    setAction('ALL');
    setDocumentId('');
    setPage(0);
  }, []);

  const handleExport = useCallback(() => {
    if (!data || data.data.length === 0) return;
    const csv = toCsv(data.data);
    const date = new Date().toISOString().split('T')[0];
    downloadAsCsv(csv, `documents-audit-${date}-page${page + 1}.csv`);
    toast.success(t('documents.audit.exportSuccess'));
  }, [data, page, t]);

  const totalPages = data ? Math.max(1, Math.ceil(data.meta.total / pageSize)) : 1;
  const hasResults = data !== undefined && data.data.length > 0;

  return (
    <div className="mx-auto max-w-7xl p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('documents.audit.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('documents.audit.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
            aria-label={t('documents.audit.refresh')}
          >
            {isFetching ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
            )}
            {t('documents.audit.refresh')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={!hasResults}
            aria-label={t('documents.audit.exportCsv')}
          >
            <Download className="mr-1.5 h-3.5 w-3.5" />
            {t('documents.audit.exportCsv')}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 rounded-lg border border-border bg-card p-4">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground">
          <Filter className="h-4 w-4 text-muted-foreground" />
          {t('documents.audit.filters')}
        </div>
        <div className="grid gap-3 md:grid-cols-4">
          {/* Action dropdown */}
          <div>
            <label
              htmlFor="audit-action"
              className="mb-1 block text-xs font-medium text-muted-foreground"
            >
              {t('documents.audit.filterAction')}
            </label>
            <select
              id="audit-action"
              value={action}
              onChange={e => {
                setAction(e.target.value as DocumentAuditAction | 'ALL');
                setPage(0);
              }}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="ALL">{t('documents.audit.allActions')}</option>
              {AUDIT_ACTIONS.map(a => (
                <option key={a} value={a}>
                  {t(`documents.audit.action.${a}`)}
                </option>
              ))}
            </select>
          </div>

          {/* Document ID (UUID) input */}
          <div className="md:col-span-2">
            <label
              htmlFor="audit-doc-id"
              className="mb-1 block text-xs font-medium text-muted-foreground"
            >
              {t('documents.audit.filterDocumentId')}
            </label>
            <input
              id="audit-doc-id"
              type="text"
              value={documentId}
              onChange={e => {
                setDocumentId(e.target.value);
                setPage(0);
              }}
              placeholder="00000000-0000-0000-0000-000000000000"
              aria-invalid={!documentIdIsValid}
              className={`w-full rounded-md border bg-background px-3 py-2 font-mono text-xs focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring ${
                documentIdIsValid ? 'border-border' : 'border-red-500 dark:border-red-400'
              }`}
            />
            {!documentIdIsValid && (
              <p className="mt-1 text-[11px] text-red-500 dark:text-red-400">
                {t('documents.audit.invalidUuid')}
              </p>
            )}
          </div>

          {/* Page size */}
          <div>
            <label
              htmlFor="audit-page-size"
              className="mb-1 block text-xs font-medium text-muted-foreground"
            >
              {t('documents.audit.pageSize')}
            </label>
            <select
              id="audit-page-size"
              value={pageSize}
              onChange={e => {
                setPageSize(Number.parseInt(e.target.value, 10));
                setPage(0);
              }}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {PAGE_SIZE_OPTIONS.map(size => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        </div>

        {(action !== 'ALL' || documentId.trim() !== '') && (
          <Button variant="ghost" size="sm" onClick={handleResetFilters} className="mt-3">
            {t('documents.audit.clearFilters')}
          </Button>
        )}
      </div>

      {/* Loading / Error / Empty / Table */}
      {isLoading && (
        <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          {t('documents.audit.loading')}
        </div>
      )}

      {isError && (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-red-200 bg-red-50 py-8 dark:border-red-800 dark:bg-red-950/20">
          <div className="flex items-center gap-2 text-red-700 dark:text-red-300">
            <AlertTriangle className="h-4 w-4" />
            <span className="text-sm font-medium">{t('documents.audit.loadError')}</span>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            {t('documents.audit.retry')}
          </Button>
        </div>
      )}

      {!isLoading && !isError && data && data.data.length === 0 && (
        <div className="rounded-lg border border-dashed border-border py-12 text-center text-sm text-muted-foreground">
          {t('documents.audit.empty')}
        </div>
      )}

      {!isLoading && !isError && hasResults && data !== undefined && (
        <>
          <div className="overflow-x-auto rounded-lg border border-border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-muted/30 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">{t('documents.audit.col.performedAt')}</th>
                  <th className="px-3 py-2 text-left">{t('documents.audit.col.action')}</th>
                  <th className="px-3 py-2 text-left">{t('documents.audit.col.documentId')}</th>
                  <th className="px-3 py-2 text-left">{t('documents.audit.col.performedBy')}</th>
                  <th className="px-3 py-2 text-left">{t('documents.audit.col.ipAddress')}</th>
                  <th className="px-3 py-2 text-left">{t('documents.audit.col.metadata')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.data.map(item => (
                  <tr key={item.id} className="hover:bg-muted/20">
                    <td className="whitespace-nowrap px-3 py-2 text-xs">
                      {formatTs(item.performedAt, locale)}
                    </td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                        {item.action}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-mono text-[11px] text-muted-foreground">
                      {item.documentId.slice(0, 8)}…
                    </td>
                    <td className="px-3 py-2 text-xs">{item.performedBy.slice(0, 8)}…</td>
                    <td className="px-3 py-2 font-mono text-[11px] text-muted-foreground">
                      {item.ipAddress ?? '—'}
                    </td>
                    <td className="max-w-xs truncate px-3 py-2 font-mono text-[11px] text-muted-foreground">
                      {item.metadata ? JSON.stringify(item.metadata) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="mt-4 flex items-center justify-between text-sm">
            <div className="text-muted-foreground">
              {t('documents.audit.pageInfo', {
                page: page + 1,
                totalPages,
                total: data.meta.total,
              })}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0 || isFetching}
              >
                {t('documents.audit.previousPage')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => p + 1)}
                disabled={(page + 1) * pageSize >= data.meta.total || isFetching}
              >
                {t('documents.audit.nextPage')}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
